import * as React from "react";
import { useLocation, Link, matchRoutes, useParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";

import {
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "components/common/Breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/common/dropdown/DropDownV2";
import { breadcrumbRoutes, type BreadcrumbNavKey } from "breadcrumbsConfig";
import { cn } from "utils/cn";
import { Skeleton } from "components/common/skeleton";
import { useProjectsApi } from "hooks/useProjects";
import { useUserCatalog } from "hooks/useSession";
import { useWorkspaceApi } from "hooks/useWorkspace";

import {
  getAccountSettingsDropdownItems,
  getNavCrumbSiblings,
  isAccountSettingsPath,
  type BreadcrumbDropdownItem,
} from "./BreadcrumbNavigation.utils";

export interface BreadcrumbNavigationProps {
  className?: string;
  routes?: typeof breadcrumbRoutes;
  /**
   * Inside a project: the project and draft switchers. They replace the
   * workspace and project crumbs, and only the page crumbs follow.
   */
  projectSwitcher?: React.ReactNode;
}

type RawCrumb = string | { label: string; href: string } | ((params: unknown) => string);

interface Crumb {
  label: string;
  href: string;
  nav?: BreadcrumbNavKey;
  /** The crumb that names the project. */
  isProject?: boolean;
  /** The label is not known yet; a placeholder bar stands in for it. */
  isPending?: boolean;
  /** Accessible name of that placeholder, e.g. "Loading project name". */
  pendingLabel?: string;
}

/**
 * The crumbs after the project, naming the page. A step crumb is dropped
 * when a page of the step follows, since the sidebar already shows the step.
 */
export const getProjectPageCrumbs = <T extends Pick<Crumb, "nav" | "isProject">>(
  crumbs: T[]
): T[] => {
  const projectIndex = crumbs.findIndex((crumb) => crumb.isProject);
  if (projectIndex < 0) return [];
  const pageCrumbs = crumbs.slice(projectIndex + 1);
  return pageCrumbs.filter(
    (crumb, index) => crumb.nav?.kind !== "step" || index === pageCrumbs.length - 1
  );
};

/** A crumb that lists its sibling pages. */
const CrumbDropdown = ({
  label,
  items,
  className,
}: {
  label: string;
  items: BreadcrumbDropdownItem[];
  className: string;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        className={cn(
          "flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors",
          className
        )}
      >
        {label}
        <ChevronDown className="w-3 h-3" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start">
      {items.map((item, itemIndex) => (
        <DropdownMenuItem key={itemIndex} asChild>
          <Link to={item.href}>{item.label}</Link>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

/** Step pages already list their siblings in the open sidebar step. */
const STEP_CRUMB_KINDS: ReadonlyArray<BreadcrumbNavKey["kind"]> = ["step", "step-item"];

/**
 * Switchers, then "/ <page>". Step pages are plain text. Project settings
 * crumbs keep their sibling list, which is how AI credits stays one click
 * away now that the sidebar leaves it out.
 */
const ProjectBreadcrumb = ({
  className,
  projectSwitcher,
  pageCrumbs,
  getSiblings,
}: {
  className?: string;
  projectSwitcher: React.ReactNode;
  pageCrumbs: Crumb[];
  getSiblings: (crumb: Crumb) => BreadcrumbDropdownItem[];
}) => (
  <Breadcrumb className={className}>
    <BreadcrumbList className="flex-nowrap gap-1 sm:gap-1.5">
      <BreadcrumbItem className="min-w-0">{projectSwitcher}</BreadcrumbItem>
      {pageCrumbs.map((crumb, index) => {
        const isLast = index === pageCrumbs.length - 1;
        const tone = isLast ? "font-semibold text-foreground" : "text-muted-foreground";
        const siblings =
          crumb.nav && !STEP_CRUMB_KINDS.includes(crumb.nav.kind) ? getSiblings(crumb) : [];
        return (
          <React.Fragment key={`${crumb.href}-${index}`}>
            <BreadcrumbSeparator className="hidden md:block">/</BreadcrumbSeparator>
            <BreadcrumbItem className="hidden min-w-0 md:inline-flex">
              {siblings.length > 0 ? (
                <CrumbDropdown label={crumb.label} items={siblings} className={tone} />
              ) : (
                <BreadcrumbLink asChild className={cn("truncate", tone)}>
                  <Link to={crumb.href} aria-current={isLast ? "page" : undefined}>
                    {crumb.label}
                  </Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          </React.Fragment>
        );
      })}
    </BreadcrumbList>
  </Breadcrumb>
);

export const BreadcrumbNavigation: React.FC<BreadcrumbNavigationProps> = ({
  className,
  routes = breadcrumbRoutes,
  projectSwitcher,
}) => {
  const location = useLocation();
  const matches = matchRoutes(routes, location);
  const { projectId, workspaceId } = useParams<{
    projectId?: string;
    workspaceId?: string;
  }>();
  const { useProject } = useProjectsApi({ take: 10, order: "DESC" });
  const { useWorkspace } = useWorkspaceApi({ take: 50 });
  const { data: project, isError: projectLoadFailed } = useProject(projectId);
  const { data: workspace } = useWorkspace(workspaceId);
  const { data: catalog } = useUserCatalog();
  // The catalog already names every project, so a newly opened project is named before its
  // own details load. With no name known yet, the crumb shows a placeholder bar.
  const projectName =
    project?.name ??
    catalog?.workspaces
      .flatMap((candidate) => candidate.projects)
      .find((candidate) => String(candidate.project_id) === projectId)?.name;
  // Same for the workspace: the catalog names it before its own details load.
  const workspaceName =
    workspace?.name ??
    catalog?.workspaces.find((candidate) => String(candidate.workspace_id) === workspaceId)?.name;

  const crumbs: Crumb[] =
    matches
      ?.filter(({ route }) => route.handle?.crumb)
      .map(({ route, params, pathname }) => {
        const raw = route.handle?.crumb as RawCrumb;
        const nav = route.handle?.nav as BreadcrumbNavKey | undefined;

        if (typeof raw === "function") {
          return { label: raw(params), href: pathname, nav };
        }
        if (typeof raw === "object" && raw.label && raw.href) {
          return { label: raw.label, href: raw.href, nav };
        }
        if (raw === "project-name") {
          // A project that failed to load has no name coming, so no placeholder either.
          return {
            label: projectName ?? (projectLoadFailed ? "Unavailable project" : ""),
            href: pathname,
            isProject: true,
            isPending: !projectName && !projectLoadFailed,
            pendingLabel: "Loading project name",
          };
        }
        if (raw === "workspace-name") {
          const id = params.workspaceId;
          return {
            label: workspaceName ?? "",
            href: id ? `/workspaces/${id}/projects` : pathname,
            isPending: !workspaceName,
            pendingLabel: "Loading workspace name",
          };
        }
        return { label: raw as string, href: pathname, nav };
      }) || [];

  if (projectSwitcher && crumbs.some((crumb) => crumb.isProject)) {
    return (
      <ProjectBreadcrumb
        className={className}
        projectSwitcher={projectSwitcher}
        pageCrumbs={getProjectPageCrumbs(crumbs)}
        getSiblings={(crumb) =>
          crumb.nav ? getNavCrumbSiblings(crumb.nav, { workspaceId, projectId }) : []
        }
      />
    );
  }

  const getDropdownItems = (crumbIndex: number): BreadcrumbDropdownItem[] => {
    const currentCrumb = crumbs[crumbIndex];
    if (!currentCrumb) {
      return [];
    }
    if (currentCrumb.nav) {
      return getNavCrumbSiblings(currentCrumb.nav, { workspaceId, projectId });
    }
    if (isAccountSettingsPath(location.pathname)) {
      return getAccountSettingsDropdownItems(crumbIndex, crumbs, currentCrumb);
    }
    return [];
  };

  return (
    <Breadcrumb className={className}>
      <BreadcrumbList>
        {crumbs.map((breadcrumb, index) => {
          const dropdownItems = getDropdownItems(index);
          const isLast = index === crumbs.length - 1;
          const hasDropdown = dropdownItems.length > 0;

          return (
            <React.Fragment key={index}>
              <BreadcrumbItem>
                {hasDropdown ? (
                  <CrumbDropdown
                    label={breadcrumb.label}
                    items={dropdownItems}
                    className={isLast ? "text-foreground font-medium" : "text-muted-foreground"}
                  />
                ) : (
                  <BreadcrumbLink
                    asChild
                    className={isLast ? "text-foreground font-medium" : "text-muted-foreground"}
                  >
                    <Link to={breadcrumb.href} aria-current={isLast ? "page" : undefined}>
                      {breadcrumb.isPending ? (
                        <Skeleton
                          role="status"
                          aria-label={breadcrumb.pendingLabel}
                          className="inline-block h-4 w-24 align-middle"
                        />
                      ) : (
                        breadcrumb.label
                      )}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {index < crumbs.length - 1 && <BreadcrumbSeparator className="hidden md:block" />}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
